<template>
  <Teleport v-if="show" to="body">
    <div class="maintenance-overlay" role="alertdialog" aria-modal="true" aria-labelledby="maintenance-title">
      <!-- alertdialog (not alert) because this is a non-dismissible
           page-block, not a transient announcement; aria-modal keeps
           screen readers from announcing the underlying app content
           behind the overlay. -->

      <div class="maintenance-glow"></div>

      <div class="maintenance-card">
        <div class="maintenance-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>
        </div>

        <h1 id="maintenance-title" class="maintenance-title">{{ title }}</h1>
        <p class="maintenance-subtitle">{{ subtitle }}</p>

        <div v-if="message" class="maintenance-message">
          <p>{{ message }}</p>
        </div>

        <div class="maintenance-meta">
          <span class="dot"></span>
          <span>{{ tenantName }}</span>
        </div>

        <div class="maintenance-actions">
          <button class="retry-button" @click="reload">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            <span>{{ retryLabel }}</span>
          </button>
          <NuxtLink v-if="!isAuthRoute" :to="loginPath" class="admin-link">
            {{ adminLoginLabel }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
const { tenant, appName } = useTenantTheme()
const { user, loggedIn } = useUserSession()
const route = useRoute()
const localePath = useLocalePath()

const message = computed(() => tenant.value?.maintenanceMode?.message || '')
const tenantName = computed(() => appName.value)

const isAdmin = computed(() => {
  if (!loggedIn.value) return false
  const role = (user.value as any)?.role
  return role === 'admin' || role === 'superadmin' || (user.value as any)?.isSuperAdmin === true
})

// Locale-aware login target so German users go to /de/auth instead of /auth.
const loginPath = computed(() => localePath('/login'))

// Don't blanket the login surface — admins need to be able to log in to
// flip the switch back. Strip an optional `/xx` i18n prefix so the check
// works for non-default locales too. `/login` immediately redirects to
// `/auth`, so both paths must be allowed or the overlay re-appears on
// the redirect target and looks like the login button bounced back.
const isAuthRoute = computed(() => {
  const raw = route.path || ''
  const p = raw.replace(/^\/[a-z]{2}(?=\/|$)/, '') || '/'
  return (
    p.startsWith('/login')
    || p.startsWith('/auth')
    || p.startsWith('/register')
    || p.startsWith('/forgot-password')
    || p.startsWith('/reset-password')
    || p.startsWith('/verify')
    || p === '/maintenance'
  )
})

const show = computed(() =>
  !!tenant.value?.maintenanceMode?.enabled
  && !isAdmin.value
  && !isAuthRoute.value
)

const { locale } = useI18n()
const labels = {
  en: {
    title: 'We\'ll be right back',
    subtitle: 'Scheduled maintenance is in progress.',
    retry: 'Try again',
    adminLogin: 'Admin login'
  },
  de: {
    title: 'Wir sind gleich wieder da',
    subtitle: 'Wir führen gerade Wartungsarbeiten durch.',
    retry: 'Erneut versuchen',
    adminLogin: 'Admin-Login'
  },
  pt: {
    title: 'Voltamos já',
    subtitle: 'Uma manutenção programada está em andamento.',
    retry: 'Tentar novamente',
    adminLogin: 'Login de admin'
  },
  es: {
    title: 'Volvemos enseguida',
    subtitle: 'Hay un mantenimiento programado en curso.',
    retry: 'Reintentar',
    adminLogin: 'Acceso de administrador'
  }
} as const

const pack = computed(() => labels[locale.value as keyof typeof labels] || labels.en)
const title = computed(() => pack.value.title)
const subtitle = computed(() => pack.value.subtitle)
const retryLabel = computed(() => pack.value.retry)
const adminLoginLabel = computed(() => pack.value.adminLogin)

const reload = () => {
  if (typeof window !== 'undefined') window.location.reload()
}
</script>

<style scoped>
.maintenance-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: radial-gradient(circle at 30% 20%, rgba(0, 242, 255, 0.08), transparent 60%),
              radial-gradient(circle at 70% 80%, rgba(255, 107, 107, 0.08), transparent 60%),
              var(--bg-color, #030304);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  animation: fadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

.maintenance-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(0, 242, 255, 0.04), transparent 70%);
  pointer-events: none;
}

.maintenance-card {
  position: relative;
  max-width: 480px;
  width: 100%;
  padding: 40px 32px;
  background: rgba(13, 14, 18, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow:
    0 24px 60px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(0, 242, 255, 0.05) inset,
    0 1px 0 rgba(255, 255, 255, 0.04) inset;
  text-align: center;
  animation: cardIn 0.45s cubic-bezier(0.16, 1, 0.3, 1) 0.05s both;
}

@keyframes cardIn {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.maintenance-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(0, 242, 255, 0.18), rgba(0, 242, 255, 0.05));
  border: 1px solid rgba(0, 242, 255, 0.25);
  color: var(--primary, #00f2ff);
  box-shadow: 0 0 24px rgba(0, 242, 255, 0.18);
}

.maintenance-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 8px;
  letter-spacing: -0.01em;
}

.maintenance-subtitle {
  font-size: 0.9rem;
  color: var(--text-muted, #8a8b95);
  margin: 0 0 20px;
  line-height: 1.5;
}

.maintenance-message {
  margin: 0 0 20px;
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  text-align: left;
}

.maintenance-message p {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
}

.maintenance-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  font-size: 0.7rem;
  color: var(--text-muted, #8a8b95);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 24px;
}

.dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--primary, #00f2ff);
  box-shadow: 0 0 8px var(--primary, #00f2ff);
  animation: pulse 1.6s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.5; transform: scale(0.85); }
}

.maintenance-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.retry-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: rgba(0, 242, 255, 0.12);
  border: 1px solid rgba(0, 242, 255, 0.3);
  color: var(--primary, #00f2ff);
  cursor: pointer;
  transition: all 0.2s ease;
}

.retry-button:hover {
  background: rgba(0, 242, 255, 0.2);
  border-color: rgba(0, 242, 255, 0.5);
  box-shadow: 0 0 16px rgba(0, 242, 255, 0.25);
}

.admin-link {
  font-size: 0.75rem;
  color: var(--text-muted, #8a8b95);
  text-decoration: none;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.admin-link:hover {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.1);
}

@media (max-width: 600px) {
  .maintenance-overlay { padding: 16px; }
  .maintenance-card { padding: 32px 24px; }
}

@media (max-width: 380px) {
  .maintenance-card { padding: 28px 18px; }
  .maintenance-title { font-size: 1.25rem; }
  .maintenance-icon { width: 56px; height: 56px; margin-bottom: 16px; }
}
</style>
